import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { IconX, IconPhoto, IconTrash, IconSettings, IconQrcode, IconDownload, IconPrinter, IconChevronDown, IconCheck, IconFileTypePdf, IconLock } from '@tabler/icons-react';
import styles from './GenerateImage.module.scss';
import { Dialog, DialogContent, DialogTitle } from '@/components/ui/dialog';
import { Tooltip, TooltipTrigger, TooltipContent, TooltipProvider } from '@/components/ui/tooltip';
import photoCollectorService from '../../../services/photoCollectorService';
import questionPaperService from '../../../services/questionPaperService';
import { BoothPosterPreview, downloadBoothPoster, printBoothPoster, readBoothPosterPrefs } from '../../../components/common/BoothPoster';
import BoothPdfFace from '../../../components/common/BoothPdfFace';
import ConfirmDialog from '../../../components/common/ConfirmDialog';
import { PRINT_READY_PATH } from '../../../config/boothTools';

// Western → Bengali digits when the UI language is Bangla.
const toBn = (n, lang) => lang === 'bn' ? String(n).replace(/[0-9]/g, d => '০১২৩৪৫৬৭৮৯'[+d]) : String(n);

/**
 * Live Photo Booth queue for the generate-image right sidebar (one office space).
 * `onPick(id)` loads the full photo into the editor; `refreshKey` forces a re-fetch
 * after a generation consumes a photo. A QR button opens the poster popup; Clear-all
 * asks for confirmation first. `collapsible` adds a chevron that folds the photo list
 * away (open by default) — the header with the live count stays visible.
 *
 * `selectMode` (multi-pick, e.g. bulk edit): taps TOGGLE a selection instead of
 * loading immediately; a সিলেক্ট button then hands every chosen id (and its item) to
 * `onSelectMany(ids, items)` at once. `markedIds` are photos the host ALREADY took (e.g.
 * sitting in the bulk batch) — shown checked and not re-selectable.
 *
 * `kinds` says what this host can work with: ['image'] (default — every photo editor) shows the
 * photos only, ['image', 'file'] adds the PDF files the customers send. `onPick(id, item)` gets the
 * queue item too, so a host can tell a photo from a file and whether the customer marked it private.
 */
const IMAGES_ONLY = ['image'];

export default function PhotoCollectorPanel({ officeSpaceId, shopName, shopLogo, shopPhone, shopAddress, onPick, refreshKey = 0, collapsible = false, selectMode = false, onSelectMany, markedIds, onCount, kinds = IMAGES_ONLY }) {
    const { t, i18n } = useTranslation();
    const [open, setOpen] = useState(true);
    const [sel, setSel] = useState(() => new Set()); // selectMode: chosen upload ids
    const [busyMany, setBusyMany] = useState(false);
    const [queue, setQueue] = useState([]);
    const [maxSize, setMaxSize] = useState(15);
    const [uploadUrl, setUploadUrl] = useState(null);
    const [slug, setSlug] = useState(null);
    const [acceptFiles, setAcceptFiles] = useState(false); // owner setting: also receive PDFs (poster title + hints follow it)
    const [loadingId, setLoadingId] = useState(null);
    const [thumbUrls, setThumbUrls] = useState({});
    const [logoDataUrl, setLogoDataUrl] = useState(null);
    const [showQr, setShowQr] = useState(false);
    const [showClear, setShowClear] = useState(false);
    const [thumbRetry, setThumbRetry] = useState(0);
    const thumbs = useRef(new Map());
    // True while THIS instance is mounted (set in setup so StrictMode's simulated
    // remount restores it) — lets late thumbnail fetches commit instead of being
    // dropped by per-effect-run cancellation.
    const mountedRef = useRef(true);
    useEffect(() => { mountedRef.current = true; return () => { mountedRef.current = false; }; }, []);

    // Only what this host can work with; the booth total (header count) still counts everything.
    const showFiles = kinds.includes('file');
    const visible = useMemo(
        () => queue.filter((q) => kinds.includes(q.kind === 'file' ? 'file' : 'image')),
        [queue, kinds],
    );
    const hiddenCount = queue.length - visible.length;

    const fetchAll = useCallback(async () => {
        if (!officeSpaceId) return;
        try {
            const res = await photoCollectorService.get(officeSpaceId);
            const d = res?.data || {};
            if (d.collector?.maxQueueSize) setMaxSize(d.collector.maxQueueSize);
            setUploadUrl(d.collector?.uploadUrl || null);
            setSlug(d.collector?.slug || null);
            setAcceptFiles(d.collector?.acceptFiles === true);
            setQueue(Array.isArray(d.queue) ? d.queue : []);
        } catch { /* ignore */ }
    }, [officeSpaceId]);

    useEffect(() => { fetchAll(); }, [fetchAll, refreshKey]);

    useEffect(() => {
        if (!officeSpaceId) return;
        const iv = setInterval(async () => {
            try {
                const res = await photoCollectorService.getQueue(officeSpaceId);
                const next = res?.data?.queue || [];
                // Only swap state when membership actually changed — a fresh array
                // every tick re-runs the thumbnail effect for nothing.
                setQueue(prev => (prev.length === next.length && prev.every((p, i) => p.id === next[i]?.id)) ? prev : next);
            } catch { /* ignore */ }
        }, 5000);
        return () => clearInterval(iv);
    }, [officeSpaceId]);

    // Proxy the R2 shop logo → data URL (untainted canvas for download/print).
    useEffect(() => {
        let alive = true;
        if (!shopLogo) { setLogoDataUrl(null); return; }
        questionPaperService.proxyImageDataUrl(shopLogo).then(d => { if (alive) setLogoDataUrl(d); }).catch(() => {});
        return () => { alive = false; };
    }, [shopLogo]);

    // Thumbnails (auth blob → object URL). No per-run cancellation: a fetch that
    // spans a queue refresh must still COMMIT its blob, otherwise the stale null
    // placeholder in `thumbs` blocks every future attempt and the tile stays a
    // skeleton forever. Only a real unmount discards results; failures clear the
    // placeholder and schedule a retry.
    useEffect(() => {
        const ids = new Set(queue.map(q => q.id));
        for (const [id, url] of thumbs.current) {
            if (!ids.has(id)) {
                if (url) URL.revokeObjectURL(url);
                thumbs.current.delete(id);
                setThumbUrls(prev => { const n = { ...prev }; delete n[id]; return n; });
            }
        }
        visible.forEach(async (q) => {
            if (q.kind === 'file' || thumbs.current.has(q.id)) return; // a file has no picture to fetch
            thumbs.current.set(q.id, null);
            try {
                const blob = await photoCollectorService.imageBlob(q.id, true);
                const url = URL.createObjectURL(blob);
                if (!mountedRef.current) { URL.revokeObjectURL(url); return; }
                thumbs.current.set(q.id, url);
                setThumbUrls(prev => ({ ...prev, [q.id]: url }));
            } catch {
                thumbs.current.delete(q.id);
                if (mountedRef.current) setTimeout(() => { if (mountedRef.current) setThumbRetry(x => x + 1); }, 3000);
            }
        });
    }, [queue, visible, thumbRetry]);

    useEffect(() => () => { for (const url of thumbs.current.values()) if (url) URL.revokeObjectURL(url); }, []);

    // Report the live count up so a host (e.g. the mobile floating pill) can show a
    // badge without mounting its own poller. Kept in an effect so it fires on every
    // queue/maxSize change, not just render.
    const onCountRef = useRef(onCount);
    onCountRef.current = onCount;
    useEffect(() => { onCountRef.current?.(queue.length, maxSize); }, [queue.length, maxSize]);

    const remove = async (id) => {
        try {
            await photoCollectorService.removeUpload(id);
            setQueue(q => q.filter(x => x.id !== id));
            const url = thumbs.current.get(id);
            if (url) URL.revokeObjectURL(url);
            thumbs.current.delete(id);
        } catch { /* ignore */ }
    };

    const doClearAll = async () => {
        setShowClear(false);
        if (!officeSpaceId || !visible.length) return;
        try {
            if (!hiddenCount) {
                await photoCollectorService.clearQueue(officeSpaceId);
                setQueue([]);
            } else {
                // Some of the booth is not shown here (the PDFs, in a photo editor): clear only what IS shown.
                const ids = visible.map((q) => q.id);
                await Promise.allSettled(ids.map((id) => photoCollectorService.removeUpload(id)));
                setQueue((q) => q.filter((x) => !ids.includes(x.id)));
            }
        } catch { /* ignore */ }
    };

    // One at a time: a second tap while the first file is still loading (a PDF can take seconds) must not bring the same file in twice.
    // A ref, not the loadingId state — two taps in the same tick both see the state as it was before either.
    const pickingRef = useRef(false);
    const pick = async (item) => {
        if (!onPick || pickingRef.current) return;
        pickingRef.current = true;
        setLoadingId(item.id);
        try { await onPick(item.id, item); } finally { pickingRef.current = false; setLoadingId(null); }
    };

    // Select-mode: taps toggle membership; the সিলেক্ট button commits all at once.
    // Photos the host already took stay checked and can't be re-selected.
    const marked = new Set(markedIds || []);
    const toggleSel = (id) => setSel(prev => {
        const next = new Set(prev);
        if (next.has(id)) next.delete(id); else next.add(id);
        return next;
    });
    // Drop selections whose photo left the queue (deleted / consumed elsewhere).
    useEffect(() => {
        setSel(prev => {
            const ids = new Set(queue.map(q => q.id));
            const kept = [...prev].filter(id => ids.has(id));
            return kept.length === prev.size ? prev : new Set(kept);
        });
    }, [queue]);
    const confirmMany = async () => {
        if (!onSelectMany || !sel.size || busyMany) return;
        setBusyMany(true);
        try {
            const ids = [...sel];
            await onSelectMany(ids, ids.map((id) => queue.find((q) => q.id === id)));
            setSel(new Set());
        } finally { setBusyMany(false); }
    };

    // Respect the poster display prefs chosen on the Settings page (logo/address/phone).
    const posterPrefs = readBoothPosterPrefs(officeSpaceId);
    const posterOpts = () => ({
        uploadUrl, shopName, t,
        shopPhone: posterPrefs.showPhone ? shopPhone : '',
        shopLogoDataUrl: posterPrefs.showLogo ? logoDataUrl : null,
        shopAddress: posterPrefs.showAddress ? shopAddress : '',
        acceptFiles,
    });
    const doDownload = () => { if (uploadUrl) downloadBoothPoster(posterOpts(), slug).catch(() => {}); };
    const doPrint = () => { if (uploadUrl) printBoothPoster(posterOpts()).catch(() => {}); };

    if (!officeSpaceId) return null;
    const count = queue.length;
    const nCount = (n) => toBn(n, i18n.language);

    return (
        <div>
            {/* Header — flat section title, same style as the left column sections */}
            <div className="flex items-center gap-1">
                <span className={styles.sectionTitle} style={{ flex: 'none' }}>{t('photoCollectorPanel.title')}</span>
                <span className="relative flex h-1.5 w-1.5 flex-none">
                    <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75" />
                    <span className="relative inline-flex rounded-full h-1.5 w-1.5 bg-emerald-500" />
                </span>
                <span className="text-[10px] font-bold px-1.5 py-0.5 rounded-full bg-primary/10 text-primary tabular-nums leading-none flex-none">{toBn(count, i18n.language)}/{toBn(maxSize, i18n.language)}</span>
                <div className="flex-1" />
                <TooltipProvider delayDuration={150}>
                    <Tooltip>
                        <TooltipTrigger asChild>
                            <button onClick={() => setShowQr(true)}
                                className="flex-shrink-0 w-7 h-7 inline-flex items-center justify-center rounded-lg text-zinc-400 hover:text-primary hover:bg-primary/10 transition-colors">
                                <IconQrcode size={15} />
                            </button>
                        </TooltipTrigger>
                        <TooltipContent>{t('photoCollectorPanel.boothQr')}</TooltipContent>
                    </Tooltip>
                    <Tooltip>
                        <TooltipTrigger asChild>
                            <Link to="/settings?tab=photoeditsettings"
                                className="flex-shrink-0 w-7 h-7 inline-flex items-center justify-center rounded-lg text-zinc-400 hover:text-primary hover:bg-primary/10 transition-colors">
                                <IconSettings size={15} />
                            </Link>
                        </TooltipTrigger>
                        <TooltipContent>{t('photoCollectorPanel.manage')}</TooltipContent>
                    </Tooltip>
                </TooltipProvider>
                {collapsible && (
                    <button onClick={() => setOpen(o => !o)} aria-expanded={open}
                        className="flex-shrink-0 w-7 h-7 inline-flex items-center justify-center rounded-lg text-zinc-400 hover:text-primary hover:bg-primary/10 transition-colors">
                        <IconChevronDown size={15} className={`transition-transform duration-200 ${open ? '' : '-rotate-90'}`} />
                    </button>
                )}
            </div>

            {/* Body — foldable when collapsible (header + count stay visible) */}
            {(!collapsible || open) && (visible.length === 0 ? (
                // Compact one-liner; the longer how-it-works hint moves to a hover title.
                <div className="mt-2 px-3 py-2.5 flex flex-col items-center justify-center gap-1.5 rounded-xl border border-dashed border-zinc-200 dark:border-zinc-700" title={t(acceptFiles ? 'photoCollectorPanel.emptyHintFiles' : 'photoCollectorPanel.emptyHint')}>
                    {hiddenCount > 0 ? (
                        // Nothing this tool can use — but the booth holds PDFs: point to the tool that prints them.
                        <>
                            <p className="flex items-center gap-2 text-[11px] font-semibold text-zinc-500 dark:text-zinc-400">
                                <IconFileTypePdf size={15} className="flex-none text-red-500" />
                                {t('photoCollectorPanel.moreFilesHint', { count: hiddenCount, n: nCount(hiddenCount) })}
                            </p>
                            <Link to={PRINT_READY_PATH} className="text-[11px] font-semibold text-primary hover:underline">{t('photoCollectorPanel.openPrintReady')}</Link>
                        </>
                    ) : (
                        <p className="flex items-center gap-2 text-[11px] font-semibold text-zinc-500 dark:text-zinc-400">
                            <IconQrcode size={15} className="flex-none text-primary" />
                            {t(showFiles ? 'photoCollectorPanel.emptyTitleAll' : 'photoCollectorPanel.emptyTitle')}
                        </p>
                    )}
                </div>
            ) : (
                <div className="mt-2">
                    <TooltipProvider delayDuration={200}>
                        {/* All items shown — the sidebar itself scrolls, not this grid */}
                        <div className="grid grid-cols-3 gap-2">
                            {visible.map(q => {
                                const isFile = q.kind === 'file';
                                const picked = selectMode && (sel.has(q.id) || marked.has(q.id));
                                return (
                                    <div key={q.id} className={`relative group rounded-lg overflow-hidden border bg-zinc-100 dark:bg-zinc-800 aspect-[209/256] shadow-sm ${picked ? 'border-primary ring-2 ring-primary/40' : 'border-zinc-200 dark:border-zinc-700'}`}>
                                        <Tooltip>
                                            <TooltipTrigger asChild>
                                                <button onClick={() => (selectMode ? (!marked.has(q.id) && toggleSel(q.id)) : pick(q))} className="w-full h-full">
                                                    {isFile ? (
                                                        <BoothPdfFace
                                                            name={q.originalName || 'PDF'}
                                                            detail={q.pageCount > 0 ? t('photoCollectorPanel.pagesCount', { count: q.pageCount, n: nCount(q.pageCount) }) : null}
                                                            size="xs"
                                                        />
                                                    ) : thumbUrls[q.id]
                                                        ? <img src={thumbUrls[q.id]} alt="" className="w-full h-full object-cover transition-transform duration-200 group-hover:scale-105" />
                                                        : (
                                                            <span className="w-full h-full flex items-center justify-center animate-pulse bg-zinc-200 dark:bg-zinc-700">
                                                                <IconPhoto size={18} className="text-zinc-300 dark:text-zinc-500" />
                                                            </span>
                                                        )}
                                                    <span className="absolute inset-0 bg-primary/0 group-hover:bg-primary/10 transition-colors" />
                                                    {picked && (
                                                        <span className="absolute top-1 left-1 w-4 h-4 rounded-full bg-primary text-white flex items-center justify-center shadow">
                                                            <IconCheck size={10} strokeWidth={3.5} />
                                                        </span>
                                                    )}
                                                    {loadingId === q.id && (
                                                        <span className="absolute inset-0 flex items-center justify-center bg-black/45">
                                                            <span className="w-5 h-5 border-2 border-white/40 border-t-white rounded-full animate-spin" />
                                                        </span>
                                                    )}
                                                </button>
                                            </TooltipTrigger>
                                            <TooltipContent side="top" className="text-center">
                                                {(isFile ? q.originalName : q.uploaderName) && <p className="max-w-[180px] break-words font-semibold">{isFile ? q.originalName : q.uploaderName}</p>}
                                                <p className="tabular-nums opacity-80">{q.uploaderPhone}</p>
                                                {q.isPrivate && <p className="mt-0.5 text-amber-300">{t('photoCollectorPanel.privateBadge')}</p>}
                                            </TooltipContent>
                                        </Tooltip>
                                        {/* The customer asked for this one to be deleted once it has been printed / used */}
                                        {q.isPrivate && (
                                            <span role="img" className={`absolute top-1 w-4 h-4 rounded-full bg-primary text-white flex items-center justify-center shadow pointer-events-none ${picked ? 'left-6' : 'left-1'}`} aria-label={t('photoCollectorPanel.privateBadge')}>
                                                <IconLock size={9} strokeWidth={3} />
                                            </span>
                                        )}
                                        <div className="absolute bottom-0 inset-x-0 px-1.5 py-1 bg-gradient-to-t from-black/75 via-black/25 to-transparent pointer-events-none">
                                            {/* Name on the tile (phone stays in the hover tooltip; legacy entries may lack a name) */}
                                            <p className="text-[9px] font-semibold text-white leading-tight truncate">{q.uploaderName || q.uploaderPhone}</p>
                                        </div>
                                        <button onClick={() => remove(q.id)} title={t('photoCollectorPanel.remove')}
                                            className="absolute top-1 right-1 w-5 h-5 flex items-center justify-center rounded-full bg-black/55 text-white opacity-0 group-hover:opacity-100 transition-opacity hover:bg-red-500">
                                            <IconX size={10} />
                                        </button>
                                    </div>
                                );
                            })}
                        </div>
                    </TooltipProvider>
                    {selectMode && sel.size > 0 && (
                        <button onClick={confirmMany} disabled={busyMany}
                            className="mt-2 w-full inline-flex items-center justify-center gap-1.5 px-4 py-2 bg-primary hover:bg-primary/90 text-white text-xs font-semibold rounded-lg transition-colors disabled:opacity-60 disabled:cursor-not-allowed">
                            {busyMany
                                ? <span className="w-3.5 h-3.5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                                : <IconCheck size={14} />}
                            {t('photoCollectorPanel.selectBtn')} ({toBn(sel.size, i18n.language)})
                        </button>
                    )}
                    {/* PDFs wait in the booth too, but this tool can't open them — say where they can be printed */}
                    {hiddenCount > 0 && (
                        <p className="mt-2 flex flex-wrap items-center gap-x-1.5 px-0.5 text-[10px] text-zinc-500 dark:text-zinc-400">
                            <IconFileTypePdf size={12} className="flex-none text-red-500" />
                            <span>{t('photoCollectorPanel.moreFilesHint', { count: hiddenCount, n: nCount(hiddenCount) })}</span>
                            <Link to={PRINT_READY_PATH} className="font-semibold text-primary hover:underline">{t('photoCollectorPanel.openPrintReady')}</Link>
                        </p>
                    )}
                    <div className="flex items-center justify-between mt-2 px-0.5">
                        <span className="text-[10px] text-zinc-400 dark:text-zinc-500">{t(selectMode ? 'photoCollectorPanel.selectHint' : showFiles ? 'photoCollectorPanel.tapToLoadAll' : 'photoCollectorPanel.tapToLoad')}</span>
                        <button onClick={() => setShowClear(true)} className="inline-flex items-center gap-1 text-[10px] font-medium text-zinc-400 hover:text-red-500 transition-colors">
                            <IconTrash size={11} /> {t('photoCollectorPanel.clearAllShort')}
                        </button>
                    </div>
                </div>
            ))}

            {/* QR / poster popup */}
            <Dialog open={showQr} onOpenChange={setShowQr}>
                <DialogContent className="max-w-[420px]" showClose={false}>
                    <button onClick={() => setShowQr(false)} aria-label="Close"
                        className="absolute right-3 top-3 z-10 w-7 h-7 inline-flex items-center justify-center rounded-full bg-red-500 hover:bg-red-600 text-white shadow-sm transition-colors">
                        <IconX size={15} />
                    </button>
                    <DialogTitle className="text-sm font-semibold text-zinc-900 dark:text-zinc-100">{t('photoCollectorPanel.boothQr')}</DialogTitle>
                    <div className="flex flex-col gap-3 mt-1">
                        <BoothPosterPreview className="w-full" uploadUrl={uploadUrl} shopName={shopName}
                            shopLogoDataUrl={posterPrefs.showLogo ? logoDataUrl : null}
                            shopPhone={posterPrefs.showPhone ? shopPhone : ''}
                            shopAddress={posterPrefs.showAddress ? shopAddress : ''}
                            acceptFiles={acceptFiles} />
                        <div className="grid grid-cols-2 gap-2">
                            <button onClick={doDownload} className="inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg text-sm font-semibold text-white bg-green-600 hover:bg-green-700 transition-colors">
                                <IconDownload size={16} /> {t('photoCollectorPanel.poster')}
                            </button>
                            <button onClick={doPrint} className="inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg text-sm font-semibold text-white bg-blue-600 hover:bg-blue-700 transition-colors">
                                <IconPrinter size={16} /> {t('photoCollectorPanel.print')}
                            </button>
                        </div>
                    </div>
                </DialogContent>
            </Dialog>

            {/* Clear-all confirmation (shared global dialog) */}
            <ConfirmDialog
                open={showClear}
                onClose={() => setShowClear(false)}
                onConfirm={doClearAll}
                title={t(showFiles ? 'photoCollectorPanel.clearConfirmTitleAll' : 'photoCollectorPanel.clearConfirmTitle')}
                description={t(showFiles ? 'photoCollectorPanel.clearConfirmMsgAll' : 'photoCollectorPanel.clearConfirmMsg')}
                confirmLabel={t('photoCollectorPanel.clearConfirmYes')}
                cancelLabel={t('photoCollectorPanel.cancel')}
            />
        </div>
    );
}
